import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Switch } from '../Switch';
import {
  notificationProviderService,
  NotificationProvider,
  NotificationProviderConfig,
  NotificationType,
} from '../../services/notificationProviderService';

const NOTIFICATION_TYPE_OPTIONS = [
  { label: 'Email', value: 'email' },
  { label: 'SMS (coming soon)', value: 'sms' },
];

const PROVIDER_SLUG_OPTIONS = [
  { label: 'Custom SMTP', value: 'smtp' },
  { label: 'Gmail', value: 'gmail' },
  { label: 'Mailjet', value: 'mailjet' },
  { label: 'SendGrid', value: 'sendgrid' },
  { label: 'Amazon SES', value: 'ses' },
];

const EMPTY_CONFIG: NotificationProviderConfig = {
  host: '',
  port: 587,
  secure: false,
  user: '',
  pass: '',
  fromEmail: '',
  fromName: '',
};

export const NotificationProvidersTab: React.FC = () => {
  const [providers, setProviders] = useState<NotificationProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [notificationType, setNotificationType] = useState<NotificationType>('email');
  const [providerSlug, setProviderSlug] = useState('smtp');
  const [config, setConfig] = useState<NotificationProviderConfig>(EMPTY_CONFIG);
  const [isActive, setIsActive] = useState(true);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    try {
      const result = await notificationProviderService.list();
      setProviders(result.notificationProviders || []);
    } catch (error) {
      toast.error('Failed to load notification providers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const resetForm = () => {
    setEditingId(null);
    setNotificationType('email');
    setProviderSlug('smtp');
    setConfig(EMPTY_CONFIG);
    setIsActive(true);
  };

  const handleEdit = (provider: NotificationProvider) => {
    setEditingId(provider.id);
    setNotificationType(provider.notificationType);
    setProviderSlug(provider.providerSlug);
    setConfig({ ...EMPTY_CONFIG, ...provider.config });
    setIsActive(provider.isActive);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this notification provider?')) return;
    try {
      await notificationProviderService.remove(id);
      toast.success('Notification provider deleted');
      if (editingId === id) resetForm();
      await loadProviders();
    } catch (error) {
      toast.error('Failed to delete notification provider');
    }
  };

  const handleSave = async () => {
    if (!config.host?.trim() || !config.user?.trim() || !config.fromEmail?.trim()) {
      toast.error('Host, username, and from-email are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        notificationType,
        providerSlug,
        config: { ...config, port: Number(config.port) || 587 },
        isActive,
      };
      if (editingId) {
        await notificationProviderService.update(editingId, payload);
        toast.success('Notification provider updated');
      } else {
        await notificationProviderService.create(payload);
        toast.success('Notification provider created');
      }
      resetForm();
      await loadProviders();
    } catch (error) {
      toast.error('Failed to save notification provider');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-agentic-text-primary">
          {editingId ? 'Edit Notification Provider' : 'Add Notification Provider'}
        </h3>
        <p className="mt-1 text-sm text-agentic-text-secondary">
          Configure where appointment confirmations and other notifications are sent from. Only one active
          provider per notification type is used at a time.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Select
          label="Notification Type"
          options={NOTIFICATION_TYPE_OPTIONS}
          value={notificationType}
          onChange={(e) => setNotificationType(e.target.value as NotificationType)}
        />
        <Select
          label="Provider"
          options={PROVIDER_SLUG_OPTIONS}
          value={providerSlug}
          onChange={(e) => setProviderSlug(e.target.value)}
        />
        <Input
          label="SMTP Host"
          placeholder="smtp.gmail.com"
          value={config.host || ''}
          onChange={(e) => setConfig((c) => ({ ...c, host: e.target.value }))}
        />
        <Input
          label="SMTP Port"
          type="number"
          placeholder="587"
          value={config.port ?? ''}
          onChange={(e) => setConfig((c) => ({ ...c, port: Number(e.target.value) }))}
        />
        <Input
          label="Username"
          placeholder="user@example.com"
          value={config.user || ''}
          onChange={(e) => setConfig((c) => ({ ...c, user: e.target.value }))}
        />
        <Input
          label="Password"
          type="password"
          placeholder={editingId ? 'Leave unchanged to keep current password' : 'Password'}
          value={config.pass || ''}
          onChange={(e) => setConfig((c) => ({ ...c, pass: e.target.value }))}
        />
        <Input
          label="From Email"
          placeholder="noreply@example.com"
          value={config.fromEmail || ''}
          onChange={(e) => setConfig((c) => ({ ...c, fromEmail: e.target.value }))}
        />
        <Input
          label="From Name"
          placeholder="Optional display name"
          value={config.fromName || ''}
          onChange={(e) => setConfig((c) => ({ ...c, fromName: e.target.value }))}
        />
      </div>

      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-agentic-text-primary">Use TLS (secure)</span>
          <Switch checked={!!config.secure} onChange={(v) => setConfig((c) => ({ ...c, secure: v }))} />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-agentic-text-primary">Active</span>
          <Switch checked={isActive} onChange={setIsActive} />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        {editingId ? (
          <Button variant="ghost" onClick={resetForm} disabled={saving}>
            Cancel Edit
          </Button>
        ) : null}
        <Button onClick={handleSave} disabled={saving} loading={saving}>
          {saving ? 'Saving...' : editingId ? 'Update Provider' : 'Create Provider'}
        </Button>
      </div>

      <div className="border-t border-agentic-gray-200 pt-5">
        {loading ? (
          <div className="py-4 text-agentic-text-secondary">Loading notification providers...</div>
        ) : providers.length === 0 ? (
          <div className="py-4 text-agentic-text-secondary">No notification providers configured yet.</div>
        ) : (
          <div className="space-y-3">
            {providers.map((provider) => (
              <div
                key={provider.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-agentic-gray-200 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-agentic-text-primary">
                      {PROVIDER_SLUG_OPTIONS.find((o) => o.value === provider.providerSlug)?.label || provider.providerSlug}
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                        provider.isActive ? 'bg-green-100 text-green-700' : 'bg-agentic-gray-100 text-agentic-text-secondary'
                      }`}
                    >
                      {provider.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="truncate text-sm text-agentic-text-secondary">
                    {provider.notificationType} · {provider.config?.fromEmail || provider.config?.host || '—'}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(provider)}>
                    Edit
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(provider.id)}>
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
